import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

// Stylized Canary archipelago on a 900x520 canvas (west -> east)
const ISLANDS = [
  {
    name: "El Hierro",
    label: "Cobertura",
    cx: 110, cy: 360,
    path: "M92 352 C110 344 136 350 140 366 C143 380 122 388 104 382 C88 377 80 360 92 352 Z",
    lx: 110, ly: 400, anchor: "middle",
  },
  {
    name: "La Gomera",
    label: "Cobertura",
    cx: 182, cy: 300,
    path: "M160 298 C160 282 182 274 200 282 C214 288 214 310 200 318 C184 327 160 316 160 298 Z",
    lx: 182, ly: 340, anchor: "middle",
  },
  {
    name: "La Palma",
    label: "Cobertura",
    cx: 150, cy: 200,
    path: "M148 158 C164 158 172 182 168 206 C164 232 150 252 142 244 C130 232 128 200 132 182 C135 166 140 158 148 158 Z",
    lx: 150, ly: 138, anchor: "middle",
  },
  {
    name: "Tenerife",
    label: "Cobertura",
    cx: 300, cy: 258,
    path: "M250 226 C278 220 336 226 350 242 C358 251 344 266 322 290 C306 308 293 320 286 316 C272 309 258 282 250 260 C245 244 243 229 250 226 Z",
    lx: 300, ly: 216, anchor: "middle",
  },
  {
    name: "Gran Canaria",
    label: "Sede · Telde",
    primary: true,
    cx: 460, cy: 292,
    path: "M460 250 C492 250 508 274 506 296 C504 324 480 340 454 337 C424 334 412 306 414 285 C416 264 436 250 460 250 Z",
    lx: 460, ly: 360, anchor: "middle",
  },
  {
    name: "Fuerteventura",
    label: "Cobertura",
    cx: 640, cy: 300,
    path: "M600 258 C622 248 648 264 664 290 C680 314 692 340 680 350 C668 358 646 344 628 322 C608 300 590 275 600 258 Z",
    lx: 660, ly: 372, anchor: "middle",
  },
  {
    name: "Lanzarote",
    label: "Cobertura",
    cx: 720, cy: 180,
    path: "M700 150 C722 144 742 158 744 178 C746 198 732 218 714 214 C698 210 688 190 690 173 C691 160 692 153 700 150 Z",
    lx: 760, ly: 168, anchor: "start",
  },
];

const LIST = [
  { name: "Gran Canaria", note: "Sede · Telde", primary: true },
  { name: "Tenerife", note: "Cobertura" },
  { name: "Lanzarote", note: "Cobertura" },
  { name: "Fuerteventura", note: "Cobertura" },
  { name: "La Palma", note: "Cobertura" },
  { name: "La Gomera", note: "Cobertura" },
  { name: "El Hierro", note: "Cobertura" },
];

export default function ServiceZone() {
  const base = ISLANDS.find((i) => i.primary);

  return (
    <section id="zona" className="relative py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="section-label mb-4">03 · Zona de servicio</span>
            <h2 className="font-display text-4xl sm:text-5xl text-white mt-4 leading-[0.95]">
              Servicio en <span className="text-[#00E5FF]">toda Canarias</span>, con sede en Gran Canaria.
            </h2>
            <p className="mt-5 text-[#9CA3AF] leading-relaxed">
              Mi base está en <span className="text-white">Telde (Gran Canaria)</span>,
              desde donde doy respuesta el mismo día en toda la isla. Para
              proyectos y mantenimientos también me desplazo al resto de las
              Islas Canarias. Si tu isla no aparece marcada, escríbeme
              igualmente — lo más probable es que pueda cubrirla.
            </p>

            <ul className="mt-8 space-y-3">
              {LIST.map((t) => (
                <li
                  key={t.name}
                  data-testid={`zone-island-${t.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="flex items-center justify-between border border-white/8 rounded-xl px-4 py-3 bg-[#121620]/60"
                >
                  <div className="flex items-center gap-3">
                    <MapPin
                      size={16}
                      className={t.primary ? "text-[#E57C23]" : "text-[#00E5FF]"}
                    />
                    <span className="text-white text-[15px]">{t.name}</span>
                  </div>
                  <span className="font-mono text-[11px] text-white/50">{t.note}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-white/8 bg-[#0d111a] p-4 sm:p-6">
              <svg
                viewBox="0 0 900 520"
                className="w-full h-auto"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Mapa de las Islas Canarias con zona de servicio"
              >
                <defs>
                  <pattern id="ocean" width="26" height="26" patternUnits="userSpaceOnUse">
                    <path d="M 26 0 L 0 0 0 26" fill="none" stroke="rgba(0,229,255,0.05)" strokeWidth="1" />
                  </pattern>
                  <radialGradient id="glowCyan" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.55" />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient id="glowCopper" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#E57C23" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#E57C23" stopOpacity="0" />
                  </radialGradient>
                </defs>

                <rect width="900" height="520" fill="url(#ocean)" />

                {/* Connection lines from Gran Canaria (base) to each island */}
                {ISLANDS.filter((i) => !i.primary).map((i, k) => (
                  <line
                    key={k}
                    x1={base.cx}
                    y1={base.cy}
                    x2={i.cx}
                    y2={i.cy}
                    stroke="rgba(0,229,255,0.28)"
                    strokeWidth="1"
                    strokeDasharray="3 6"
                    className="circuit-path"
                  />
                ))}

                {/* Island silhouettes */}
                {ISLANDS.map((i) => (
                  <g key={i.name}>
                    {/* glow halo */}
                    <circle
                      cx={i.cx}
                      cy={i.cy}
                      r={i.primary ? 60 : 34}
                      fill={i.primary ? "url(#glowCopper)" : "url(#glowCyan)"}
                    />
                    <path
                      d={i.path}
                      fill="#171c28"
                      stroke={i.primary ? "rgba(229,124,35,0.9)" : "rgba(0,229,255,0.35)"}
                      strokeWidth={i.primary ? "1.6" : "1"}
                    />
                    {/* marker dot */}
                    <circle cx={i.cx} cy={i.cy} r="4.5" fill={i.primary ? "#E57C23" : "#00E5FF"} />
                    {i.primary && (
                      <circle cx={i.cx} cy={i.cy} r="12" fill="none" stroke="#E57C23" strokeOpacity="0.5">
                        <animate attributeName="r" values="12; 26; 12" dur="2.6s" repeatCount="indefinite" />
                        <animate attributeName="stroke-opacity" values="0.6; 0; 0.6" dur="2.6s" repeatCount="indefinite" />
                      </circle>
                    )}
                    <text
                      x={i.lx}
                      y={i.ly}
                      textAnchor={i.anchor}
                      fontFamily="Cabinet Grotesk, sans-serif"
                      fontWeight="700"
                      fontSize="14"
                      fill={i.primary ? "#F3F4F6" : "#C6CCD6"}
                    >
                      {i.name}
                    </text>
                    {i.primary && (
                      <text
                        x={i.lx}
                        y={i.ly + 16}
                        textAnchor={i.anchor}
                        fontFamily="JetBrains Mono"
                        fontSize="10"
                        fill="#E57C23"
                        letterSpacing="1"
                      >
                        SEDE · TELDE
                      </text>
                    )}
                  </g>
                ))}

                {/* Compass */}
                <g transform="translate(70, 70)">
                  <circle r="22" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.1)" />
                  <path d="M 0 -14 L 4 4 L 0 0 L -4 4 Z" fill="#00E5FF" />
                  <text y="30" fontFamily="JetBrains Mono" fontSize="9" fill="rgba(255,255,255,0.5)" textAnchor="middle">N</text>
                </g>

                {/* Scale */}
                <g transform="translate(70, 470)">
                  <line x1="0" y1="0" x2="90" y2="0" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
                  <line x1="0" y1="-4" x2="0" y2="4" stroke="rgba(255,255,255,0.4)" />
                  <line x1="90" y1="-4" x2="90" y2="4" stroke="rgba(255,255,255,0.4)" />
                  <text y="18" fontFamily="JetBrains Mono" fontSize="9" fill="rgba(255,255,255,0.5)">100 km</text>
                </g>

                <text x="820" y="500" fontFamily="JetBrains Mono" fontSize="9" fill="rgba(255,255,255,0.3)" textAnchor="end">
                  ISLAS CANARIAS · ES-CN
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
